import * as env from "shared/env";
import type { ListingPhotoKey } from "./resource/listing-photo";

export const ping = (): string => {
	return base() + `/ping.json`;
}

export const user = (): string => {
	return base() + `/user.json`;
}

export const profileSync = (): string => {
	return base() + `/profile-sync.json`;
}

export const userAuth = (): string => {
	return base() + `/user/auth.json`;
}

export const priceRangeOptions = (): string => {
	return base() + `/price-range-options.json`;
}

export const adminMode = (): string => {
	return `${env.coldFusionUrl}/idx/controllers/AdminMode.cfm`;
}

export const oneGetUser = (): string => {
	return oneBase() + `/users/current`;
}

export const reseller = (resellerId: number): string => {
	return base() + `/reseller/${resellerId}.json`;
}

export const client = (clientId: number): string => {
	return base() + `/client/${clientId}.json`;
}

export const quota = () => {
	return base() + `/quota.json`;
}

export const leads = () => {
	return base() + `/leads.json`;
}

export const lead = (leadId?: number) => {
	return base() + `/lead/${leadId}.json`;
}

export const savedSearches = (): string => {
	return base() + `/saved-searches.json`;
}

export const savedSearch = (savedSearchId: number): string => {
	return base() + `/saved-searches/${savedSearchId}.json`;
}

export const savedSearchResultTotal = (savedSearchId: number): string => {
	return base() + `/saved-search/${savedSearchId}/result-total.json`;
}

export const savedListings = (leadId?: number): string => {
	const query = querystring({ leadId });
	return base() + `/saved-listings.json${query}`;
}

export const savedListing = (savedListingId: number): string => {
	return base() + `/saved-listings/${savedListingId}.json`;
}

export const leadSearchProfile = (leadId: number): string => {
	const query = querystring({ leadId });
	return base() + `/lead-search-profile.json${query}`;
}

export const leadConversationHistory = (leadId: number): string => {
	const query = querystring({ leadId });
	return base() + `/lead-conversation-history.json${query}`;
}

/**
 * One day of a lead's Search Agent conversation.
 *
 * `date` is passed through verbatim as the `YYYY-MM-DD` string the day resource carried.
 * The backend buckets messages by calendar day in Pacific time, so re-deriving the date
 * from a browser-local Moment would miss the bucket for anyone outside that zone.
 */
export const leadConversationHistoryDay = (leadId: number, date: string): string => {
	const query = querystring({ leadId, date });
	return base() + `/lead-conversation-history-day.json${query}`;
}

export const leadProfileSuggestions = (leadId: number): string => {
	const query = querystring({ leadId });
	return base() + `/lead-profile-suggestions.json${query}`;
}

export const leadProfileSuggestionsAccept = (): string => {
	return base() + `/lead-profile-suggestions/accept.json`;
}

export const leadProfileSuggestionsDismiss = (): string => {
	return base() + `/lead-profile-suggestions/dismiss.json`;
}

export const emailSents = (leadId?: number): string => {
	const query = querystring({ leadId });
	return base() + `/email-sents.json${query}`;
}

export const emailClicks = (leadId?: number): string => {
	const query = querystring({ leadId });
	return base() + `/email-clicks.json${query}`;
}

export const leadPhones = (): string => {
	return base() + `/lead-phones.json`;
}

export const leadPhone = (leadPhoneId: number): string => {
	return base() + `/lead-phone/${leadPhoneId}.json`;
}

export const leadPipelines = (): string => {
	return base() + `/lead-pipelines.json`;
}

export const leadPipeline = (leadPipelineId: number): string => {
	return base() + `/lead-pipelines/${leadPipelineId}.json`;
}

export const leadAnniversaries = (): string => {
	return base() + `/lead-anniversaries.json`;
}

export const leadAnniversary = (leadAnniversaryId: number): string => {
	return base() + `/lead-anniversary/${leadAnniversaryId}.json`;
}

export const leadImport = (): string => {
	return base() + `/lead-imports.json`;
}

export const leadImports = (): string => {
	return base() + `/lead-imports.json`;
}

export const leadNotes = (): string => {
	return base() + `/lead-notes.json`;
}

export const leadNote = (leadNoteId: number): string => {
	return base() + `/lead-note/${leadNoteId}.json`;
}

export const leadTags = (): string => {
	return base() + `/lead-tags.json`;
}

export const leadTag = (leadTagId: number): string => {
	return base() + `/lead-tag/${leadTagId}.json`;
}

export const leadActivities = (leadId?: number): string => {
	const query = querystring({ leadId });
	return base() + `/lead-activities.json${query}`;
}

export const leadActivityStats = (): string => {
	return base() + `/lead-activity-stats.json`;
}

export const leadLastActivities = (): string => {
	return base() + `/lead-last-activities.json`;
}

export const leadLastSearchAgentChats = (): string => {
	return base() + `/lead-last-search-agent-chats.json`;
}

export const campaigns = () => {
	return base() + `/campaigns.json`;
}

export const campaignDisplayCategories = () => {
	return base() + `/campaign-display-categories.json`;
}

// Single category by id — rename (PUT) and delete (DELETE). The DELETE `moveToId` contract is keyed
// on param PRESENCE (BE1 detects it from the raw request), so the three cases are distinct:
//   undefined ⇒ omit param      ⇒ plain delete, NO reassignment (empty category)
//   null      ⇒ "?moveToId="    ⇒ Move-&-Delete → Uncategorized (null FK)
//   number    ⇒ "?moveToId=<n>" ⇒ Move-&-Delete → that category
// (Emitting a present-but-blank param for null is what triggers the reassign; omitting it would leave
// the category's campaigns pointing at a deleted id.)
export const campaignDisplayCategory = (id: number, moveToId?: number | null) => {
	const query = moveToId === undefined ? "" : querystring({ moveToId: moveToId === null ? "" : moveToId });
	return base() + `/campaign-display-categories/${id}.json${query}`;
}

export const campaign = (campaignId: number) => {
	return base() + `/campaign/${campaignId}.json`;
}

export const geographicRoutes = () => {
	return base() + `/geographic-routes.json`;
}

export const geographicRoute = (geographicRouteId: number) => {
	return base() + `/geographic-route/${geographicRouteId}.json`;
}

export const roundRobinRoutes = () => {
	return base() + `/round-robin-routes.json`;
}


export const postalCodesRoutes = () => {
	return base() + `/postal-codes.json`;
}

export const roundRobinRoute = (roundRobinRouteId: number) => {
	return base() + `/round-robin-route/${roundRobinRouteId}.json`;
}

export const campaignTextingResendOtp = () => {
	return base() + `/campaign/rent-number/resend-otp.json`;
}

export const campaignTextingTaxId = () => {
	return base() + `/campaign/texting/tax-id.json`;
}

export const campaignMessages = () => {
	return base() + `/campaign/campaign-messages.json`;
}

export const campaignMessage = (campaignMessageId: number) => {
	return base() + `/campaign/campaign-message/${campaignMessageId}.json`;
}

export const campaignCampaignMessages = () => {
	return base() + `/campaign/campaign-campaign-messages.json`;
}

export const campaignCampaignMessage = (campaignCampaignMessageId: number) => {
	return base() + `/campaign/campaign-campaign-message/${campaignCampaignMessageId}.json`;
}

export const campaignCampaignMessageSendTestEmail = () => {
	return base() + `/campaigns/campaign-campaign-message/sendTest.json`;
}

export const campaignSubscriptions = () => {
	return base() + `/campaign/subscriptions.json`;
}

export const campaignSubscriptionsBatch = () => {
	return base() + `/campaign/subscriptions/batch.json`;
}

export const campaignSubscription = (campaignSubscriptionid: number) => {
	return base() + `/campaign/subscription/${campaignSubscriptionid}.json`;
}

export const campaignEmails = (leadId?: number, campaignId?: number, blast?: boolean) => {
	const query = querystring({ leadId, campaignId, blast });
	return base() + `/campaign-emails.json${query}`;
}

export const campaignStats = () => {
	return base() + `/campaign-stats.json`;
}

export const campaignSubscriptionStats = () => {

	return base() + `/campaign/subscription-stats.json`;
}

export const campaignCampaignMessageStats = () => {
	return base() + `/campaign-campaign-message-stats.json`;
}

export const campaignRoute = (campaignRouteId: number) => {
	return base() + `/campaign-routes/${campaignRouteId}.json`;
}

export const campaignRoutes = () => {
	return base() + `/campaign-routes.json`;
}

export const campaingsSettings = () => {
	return base() + `/campaigns-settings.json`;
}

export const markets = () => {
	return base() + `/markets.json`;
}

export const market = (id?: number) => {
	return base() + `/market/${id}.json`;
}

export const marketSettings = () => {
	return base() + `/market-settings.json`;
}

export const mobileSettings = () => {
	return base() + `/mobile-settings.json`;
}

export const marketListing = (id?: number) => {
	return base() + `/market-listing/${id}.json`;
}

export const marketListings = (): string => {
	return base() + `/market-listings.json`;
}

export const marketReports = () => {
	return base() + `/market-reports.json`;
}

export const marketReport = (id?: number) => {
	return base() + `/market-report/${id}.json`;
}

export const marketReportSubscriptions = () => {
	return base() + `/market-report/subscriptions.json`;
}

export const marketSubscriptions = (marketId: number) => {
	return base() + `/market/${marketId}/subscriptions.json`;
}

export const marketReportSubscription = (marketReportSubscriptionId: number) => {
	return base() + `/market-report/subscription/${marketReportSubscriptionId}.json`;
}

export const agent = (agentId: number) => {
	return base() + `/agent/${agentId}.json`;
}

export const agents = () => {
	return base() + `/agents.json`;
}

export const agentTestimonials = (agentId: number) => {
	return base() + `/agent/${agentId}/testimonials.json`;
}

export const agentTestimonial = (agentId: number, testimonialId: number) => {
	return base() + `/agent/${agentId}/testimonial/${testimonialId}.json`;
}

export const boards = () => {
	return base() + `/boards.json`;
};

export const agentBoard = (agentBoardId: number): string => {
	return base() + `/agent-board/${agentBoardId}.json`;
}

export const agentBoards = () => {
	return base() + `/agent-boards.json`
}

export const office = (officeId: number) => {
	return base() + `/office/${officeId}.json`;
}

export const offices = () => {
	return base() + `/offices.json`;
}

export const officeBoard = (officeBoardId: number): string => {
	return base() + `/office-board/${officeBoardId}.json`;
}

export const officeBoards = () => {
	return base() + `/office-boards.json`;
}

// strict runs the MLS ID rules on a client-board write (duplicates, a code already on Featured). Only the MLS IDs
// tab sends it; the old MLS Setup page keeps today's behaviour.
const strictQuery = (strict: boolean) => (strict ? "?strict=true" : "");

export const clientBoard = (clientBoardId: number, strict = false): string => {
	return base() + `/client-board/${clientBoardId}.json` + strictQuery(strict);
}

export const clientBoards = (strict = false) => {
	return base() + `/client-boards.json` + strictQuery(strict);
}

export const agentMlsIds = () => {
	return base() + `/agent-mls-ids.json`;
}

export const agentMlsId = (agentMlsIdId: number) => {
	return base() + `/agent-mls-id/${agentMlsIdId}.json`;
}

export const clientBoardOptions = () => {
	return base() + `/client-board-options.json`;
}

export const cities = () => {
	return base() + `/cities.json`;
}

export const searchCities = () => {
	return base() + `/search-cities.json`;
}

export const locations = () => {
	return base() + `/locations.json`;
}

export const propertyTypes = () => {
	return base() + `/property-types.json`;
}

export const propertyTypesRestore = () => {
	return base() + `/property-types/restore.json`;
}

export const neighborhoods = () => {
	return base() + `/neighborhoods.json`;
}

export const aggregateStats = () => {
	return base() + `/aggregate-stats.json`;
}

export const tasks = () => {
	return base() + `/tasks.json`;
}

export const task = (taskId: number) => {
	return base() + `/task/${taskId}.json`;
}

export const integrations = (ownerTypeId?: number) => {
	const query = ownerTypeId !== undefined ? `?ownerTypeId=${ownerTypeId}` : "";
	return base() + `/integrations.json${query}`;
}

export const integration = (integrationId: number) => {
	return base() + `/integration/${integrationId}.json`;
}

export const integrationTest = (integrationId: number) => {
	return base() + `/integration/${integrationId}/test.json`;
}

export const integrationCalendars = (integrationId: number) => {
	const query = querystring({ integrationId });
	return base() + `/integration-calendars.json${query}`;
}

export const followUpBossConnect = (integrationId: number) => base() + `/integration/${integrationId}/follow-up-boss/connect.json`;
export const followUpBossDisconnect = (integrationId: number) => base() + `/integration/${integrationId}/follow-up-boss/disconnect.json`;
export const followUpBossStatus = (integrationId: number) => base() + `/integration/${integrationId}/follow-up-boss/status.json`;
export const followUpBossStartBackfill = (integrationId: number) => base() + `/integration/${integrationId}/follow-up-boss/start-backfill.json`;
export const followUpBossAgentSelection = (integrationId: number) => base() + `/integration/${integrationId}/follow-up-boss/agent-selection.json`;

export const facebookPages = () => {
	return base() + "/facebook/pages.json";
}

export const siteLinks = () => {
	return base() + `/idx-links.json`;
}

export const sendEmail = (contactEmail: string, contactName: string, subject: string, emailBody: string) => {
	const query = querystring({ contactEmail, contactName, subject, emailBody});
	return base() + `/email-sync.json${query}`;
}

export const uploadSignature = (uploadPreset: string) => {
	return base() + `/upload-signature/${uploadPreset}.json`;
}

export const geocode = (address?: string) => {
	const query = querystring({ address });
	return base() + `/geocode.json${query}`;
}

export const pages = () => {
	return base() + `/pages.json`;
}

export const page = (pageId: number) => {
	return base() + `/page/${pageId}.json`;
}

export const languages = () => {
	return base() + `/languages.json`;
}

export const uiEvents = () => {
	return base() + `/ui-events.json`;
}

export const naturalLanguageSearchSuggestions = () => {
	return base() + `/natural-language-search-suggestions.json`;
}

export const naturalLanguageSearchSuggestion = (id: number) => {
	return base() + `/natural-language-search-suggestion/${id}.json`;
}

export const areaSelectorPlaceholders = () => {
	return base() + `/area-selector-placeholders.json`;
}

export const areaSelectorPlaceholder = (id: number) => {
	return base() + `/area-selector-placeholder/${id}.json`;
}

export const feedback = () => {
	return base() + `/feedback.json`;
}

export const feedbackCategories = () => {
	return base() + `/feedback/categories.json`;
}

export const emailRoutingRules = () => {
	return base() + `/email-routing-rules.json`;
}

export const emailRecipients = () => {
	return base() + "/email-recipients.json";
}

export const emailRecipient = (clientEmailRecipientId: number) => {
	return base() + `/email-recipients/${clientEmailRecipientId}.json`;
}

export const emailRoutingRule = (emailRoutingId: number) => {
	return base() + `/email-routing-rules/${emailRoutingId}.json`;
}

export const offMarketListings = () => {
	return base() + `/off-market-listings.json`;
}

export const offMarketListing = (id: number) => {
	return base() + `/off-market-listing/${id}.json`;
}

export const clientProperties = () => {
	return base() + `/client-properties.json`;
}

export const clientProperty = (id: number) => {
	return base() + `/client-properties/${id}.json`;
}

// Single serialization point for the listing-key union (MAX-3898) — every listing-photos endpoint
// below routes its key through here, so a future Active fork (listingNumber+boardId) is a drop-in.
const listingPhotoKeyParams = (key: ListingPhotoKey) => querystring(key);

export const listingPhotos = (key: ListingPhotoKey) => {
	return base() + `/listing-photos.json${listingPhotoKeyParams(key)}`;
}

export const listingPhotosReorder = (key: ListingPhotoKey) => {
	return base() + `/listing-photos/reorder.json${listingPhotoKeyParams(key)}`;
}

export const listingPhoto = (propertyPhotoId: number, key: ListingPhotoKey) => {
	return base() + `/listing-photo/${propertyPhotoId}.json${listingPhotoKeyParams(key)}`;
}

export const listingPhotosMain = (key: ListingPhotoKey) => {
	return base() + `/listing-photos/main.json${listingPhotoKeyParams(key)}`;
}

export const listingPhotosRevert = (key: ListingPhotoKey) => {
	return base() + `/listing-photos/revert.json${listingPhotoKeyParams(key)}`;
}

export const leadContactReport = (params: {
	startDate: string;
	endDate: string;
	typeId?: number;
	offset?: number;
	limit?: number;
	sortDir?: string;
}) => {
	return base() + `/lead-contact-report.json${querystring(params)}`;
}

export const contactEventTypes = () => {
	return base() + `/contact-event-types.json`;
}

export const savedSearchActivity = (params: {
	startDate: string;
	endDate: string;
	page?: number;
	pageSize?: number;
	sortBy?: string;
	sortDir?: string;
}) => {
	return base() + `/saved-search-activity.json${querystring(params)}`;
}

export const savedListingsReport = (startDate?: string, endDate?: string) => {
	return base() + `/reports/saved-listings.json${querystring({ startDate, endDate })}`;
}

export const mobileActivity = (params: { startDate: string; endDate: string; type?: string }) => {
	return base() + `/reports/mobile-activity.json${querystring(params)}`;
}

export const leadsExport = (params: { orderBy: string; includeDisabled: boolean; includeDeleted: boolean }) => {
	return base() + `/leads-export.csv${querystring(params)}`;
}

export const agentsExport = () => {
	return base() + `/agents-export.csv`;
}

export const emailBranding = () => {
	return base() + `/email-branding.json`;
}

export const emailBrandingPreview = () => {
	return base() + `/email-branding/preview`;
}

export const activeListings = (params: { agentId?: number; featuredOnlyYn?: boolean; activeFeaturedForReport?: boolean; context?: string }) => {
	return base() + `/listings.json${querystring(params)}`;
}

export const activeListingModify = (params: { listingNumber: string; boardId: number }) => {
	return base() + `/active-listing-modify.json${querystring(params)}`;
}

export const billing = () => {
	return base() + `/billing.json`;
}

export const billingCard = () => {
	return base() + `/billing/card.json`;
}

export const billingInvoices = () => {
	return base() + `/billing/invoices.json`;
}

export const billingInvoicePdf = (displayNumber: string) => {
	return base() + `/billing/invoices/${encodeURIComponent(displayNumber)}.pdf`;
}

const base = (): string => {
	return env.apiUrl + `/api/control-panel`;
}

const oneBase = (): string => {
	return env.oneApiUrl + "";
}

const querystring = (params: { [k: string]: any }) => {
	let result = Object
		.keys(params)
		.map((name) => {
			const value = params[name];
			if (value !== undefined && value !== null) {
				return encodeURIComponent(name) + "=" + encodeURIComponent(value);
			}
			return null;
		})
		.filter(Boolean)
		.join("&")
	;
	if (result) {
		result = "?" + result;
	}
	return result;
}
